import { useCallback, useEffect, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { useAuth } from '../auth/AuthProvider.js';
import { useSharedTabletOperator } from '../auth/SharedTabletOperatorProvider.js';
import type { ActiveSharedTabletOperator } from '../auth/operatorCredentialStore.js';
import { flushLocalDb, getMeta, setMeta } from '../db/local.js';
import { getSupabase } from '../supabase.js';
import { applyFavorite, recipeFavoritePrincipalKey } from './recipeFavoritesCore.js';

const FavoriteIdsSchema = z.array(z.string().uuid()).max(10_000);
const FavoriteCacheSchema = z.object({
  version: z.literal(1),
  favorites: FavoriteIdsSchema,
  pending: z.record(z.boolean()),
});

interface FavoriteCache {
  version: 1;
  favorites: string[];
  pending: Record<string, boolean>;
}

interface FavoriteScope {
  tenantId: string;
  principalKey: string;
  operator: ActiveSharedTabletOperator | null;
}

const EMPTY_CACHE: FavoriteCache = { version: 1, favorites: [], pending: {} };

function cacheKey(scope: Pick<FavoriteScope, 'tenantId' | 'principalKey'>): string {
  return `recipe-favorites:v1:${scope.tenantId}:${scope.principalKey}`;
}

async function readCache(scope: FavoriteScope): Promise<FavoriteCache> {
  const raw = await getMeta(cacheKey(scope));
  if (!raw) return { ...EMPTY_CACHE };
  try {
    return FavoriteCacheSchema.parse(JSON.parse(raw));
  } catch {
    return { ...EMPTY_CACHE };
  }
}

async function writeCache(scope: FavoriteScope, cache: FavoriteCache): Promise<void> {
  await setMeta(cacheKey(scope), JSON.stringify(cache));
  await flushLocalDb();
}

function rpcActor(operator: ActiveSharedTabletOperator | null): {
  p_operator_assertion: { grant_id: string; grant_token: string; tenant_personnel_id: string } | null;
  p_device_id: string | null;
} {
  return {
    p_operator_assertion: operator ? {
      grant_id: operator.grantId,
      grant_token: operator.grantToken,
      tenant_personnel_id: operator.tenantPersonnelId,
    } : null,
    p_device_id: operator?.deviceId ?? null,
  };
}

async function fetchRemote(scope: FavoriteScope): Promise<string[]> {
  const { data, error } = await getSupabase().rpc('get_my_recipe_favorites_v1', rpcActor(scope.operator));
  if (error) throw error;
  return FavoriteIdsSchema.parse(data);
}

async function setRemote(scope: FavoriteScope, recipeId: string, favorite: boolean): Promise<void> {
  const { data, error } = await getSupabase().rpc('set_my_recipe_favorite_v1', {
    p_recipe_id: recipeId,
    p_is_favorite: favorite,
    ...rpcActor(scope.operator),
  });
  if (error) throw error;
  if (data !== true) throw new Error('recipe_favorite_not_saved');
}

async function queueFavorite(
  scope: FavoriteScope,
  recipeId: string,
  favorite: boolean,
): Promise<FavoriteCache> {
  const current = await readCache(scope);
  const next: FavoriteCache = {
    version: 1,
    favorites: applyFavorite(current.favorites, recipeId, favorite),
    pending: { ...current.pending, [recipeId]: favorite },
  };
  await writeCache(scope, next);
  return next;
}

async function drainPending(scope: FavoriteScope): Promise<FavoriteCache> {
  let current = await readCache(scope);
  for (const [recipeId, favorite] of Object.entries(current.pending)) {
    await setRemote(scope, recipeId, favorite);
    const pending = { ...current.pending };
    delete pending[recipeId];
    current = { ...current, pending };
    await writeCache(scope, current);
  }
  return current;
}

async function loadFavorites(scope: FavoriteScope): Promise<string[]> {
  let local = await readCache(scope);
  if (!navigator.onLine) return local.favorites;
  try {
    local = await drainPending(scope);
    const remote = await fetchRemote(scope);
    await writeCache(scope, { ...local, favorites: remote });
    return remote;
  } catch {
    return local.favorites;
  }
}

export function useRecipeFavorites(): {
  favoriteIds: ReadonlySet<string>;
  toggleFavorite: (recipeId: string) => Promise<void>;
  ownerLabel: string | null;
} {
  const { profile } = useAuth();
  const tablet = useSharedTabletOperator();
  const queryClient = useQueryClient();
  const principalKey = profile?.id ? recipeFavoritePrincipalKey({
    userId: profile.id,
    sharedTablet: tablet.isSharedTablet,
    tenantPersonnelId: tablet.activeOperator?.tenantPersonnelId,
  }) : null;
  const scope = profile?.active_tenant_id && principalKey ? {
    tenantId: profile.active_tenant_id,
    principalKey,
    operator: tablet.isSharedTablet ? tablet.activeOperator : null,
  } : null;
  const queryKey = [
    'recipe-favorites',
    scope?.tenantId ?? null,
    scope?.principalKey ?? null,
  ] as const;
  const query = useQuery<string[]>({
    queryKey,
    networkMode: 'always', // loadFavorites restores the durable cache without a network.
    enabled: scope !== null,
    queryFn: () => loadFavorites(scope!),
  });

  useEffect(() => {
    if (!scope) return undefined;
    const refresh = (): void => { void queryClient.invalidateQueries({ queryKey }); };
    window.addEventListener('online', refresh);
    return () => window.removeEventListener('online', refresh);
  }, [queryClient, scope?.principalKey, scope?.tenantId]);

  const favoriteIds = useMemo(
    () => new Set(query.data ?? []),
    [query.data],
  );
  const toggleFavorite = useCallback(async (recipeId: string): Promise<void> => {
    if (!scope) throw new Error('recipe_favorite_owner_unavailable');
    const favorite = !favoriteIds.has(recipeId);
    const queued = await queueFavorite(scope, recipeId, favorite);
    queryClient.setQueryData<string[]>(queryKey, queued.favorites);
    if (!navigator.onLine) return;
    try {
      const drained = await drainPending(scope);
      queryClient.setQueryData<string[]>(queryKey, drained.favorites);
    } catch {
      // La preferenza resta nell'outbox locale e viene ritentata al ritorno
      // online o alla prossima apertura di Ricette/Etichette.
    }
  }, [favoriteIds, queryClient, scope?.operator, scope?.principalKey, scope?.tenantId]);

  return {
    favoriteIds,
    toggleFavorite,
    ownerLabel: tablet.isSharedTablet ? tablet.activeOperator?.fullName ?? null : null,
  };
}
